import React, { useState, useEffect } from 'react';
import {
  Container, Grid2 as Grid, Typography, TextField, FormControl,
  InputLabel, Select, MenuItem, FormHelperText, Button, Box,
  Backdrop, CircularProgress, Modal, Stack, Paper, IconButton
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from '../../actions/url';
import { getEstados, getEstadosEEUU } from "../../actions/estados";
import { getUser } from "../../actions/users";
import { isEmpty } from '../../components/common/helpers';

const RequestInvoice = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.users.user);
  const auth = useSelector(state => state.auth);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);

  const [loading, setLoading] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const [formValues, setFormValues] = useState({
    razonSocial: "",
    rfc: "",
    regimenFiscal: "",
    monto: "",
    pais: "Mexico",
    estado: "",
    cp: "",
    delegacion: "",
    colonia: "",
    calle: "",
    numExt: "",
    numInt: ""
  });

  const [errors, setErrors] = useState({});
  const [estados, setEstados] = useState([]);
  const [municipios, setMunicipios] = useState([]);

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  // Load user data if not loaded
  useEffect(() => {
    if (auth.user && isEmpty(user)) {
      dispatch(getUser(auth.user?._id));
    }
  }, [auth.user, user, dispatch]);

  // Load states based on country
  useEffect(() => {
    (async () => {
      try {
        if (formValues.pais === "Mexico") {
          const arrEstados = await getEstados();
          setEstados(arrEstados);
        } else if (formValues.pais === "EEUU") {
          const arrEstados = await getEstadosEEUU();
          setEstados(arrEstados);
        }
      } catch (err) {
        console.error("Error loading states: ", err);
      }
    })();
  }, [formValues.pais]);

  // Load municipalities based on state (for Mexico)
  useEffect(() => {
    if (formValues.pais === "Mexico" && !isEmpty(estados) && formValues.estado) {
      const index = estados?.findIndex(edo => edo.estado === formValues.estado);
      if (index !== -1) {
        setMunicipios(estados[index]?.municipio || []);
      }
    }
  }, [formValues.estado, estados, formValues.pais]);



  const handleChange = (e) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value
    });
  };

  const validate = () => {
    let temp = {};
    if (!formValues.monto) {
      temp.monto = "El monto total es obligatorio";
    } else if (isNaN(formValues.monto) || parseFloat(formValues.monto) <= 0) {
      temp.monto = "Ingresa un monto válido mayor a 0";
    }
    if (!formValues.razonSocial) temp.razonSocial = "La razón social es obligatoria";
    if (!formValues.rfc) temp.rfc = "El RFC es obligatorio";
    if (!formValues.regimenFiscal) temp.regimenFiscal = "El régimen fiscal es obligatorio";

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      const payload = {
        ...formValues,
        monto: parseFloat(formValues.monto),
        concepto: 'Solicitud de Factura',
        idInversion: 'manual_request'
      };

      const res = await axios.post('/api/users/send/email/solicitar-factura', payload, config);

      if (res.data && res.data.success) {
        setSuccessModal(true);
      } else {
        alert("Ocurrió un error al procesar tu solicitud.");
      }
    } catch (error) {
      console.error(error);
      const errMsg = error.response?.data?.msg || error.message || "Ocurrió un error desconocido";
      alert("Ocurrió un error al enviar el correo al administrador: " + errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Modal
        open={successModal}
        className="modal-content"
        onClose={() => {
          setSuccessModal(false);
          navigate('/detalle-inversiones');
        }}
      >
        <Box className="modal-message modal-large">
          <img alt="modal-img" src="/Successmark.png" />
          <Typography className="titleMain" variant="h3" component="h3" mt={3}>
            ¡Solicitud enviada!
          </Typography>
          <Typography mt={3} mb={5}>
            Hemos recibido tu solicitud de factura. Nuestro equipo de administración la procesará a la brevedad.
          </Typography>
          <Button
            onClick={() => {
              setSuccessModal(false);
              navigate('/detalle-inversiones');
            }}
            type="button"
            variant="contained"
          >
            Entendido
          </Button>
        </Box>
      </Modal>

      <Grid container mt={{ xs: 10, md: 11 }} spacing={3}>
        <Grid size={12} mt={3}>
          <IconButton sx={{ padding: 0 }} onClick={() => navigate("/detalle-inversiones")}>
            <ArrowBackIcon sx={{ color: "black", width: "40px", height: "40px" }} />
          </IconButton>
        </Grid>

        <Grid size={12}>
          <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: '1px solid #eaeaea' }}>
            <Typography className="titleMain" variant="h4" component="h1" gutterBottom>
              Solicitar Factura
            </Typography>
            <Typography variant="body2" mb={4}>
              Completa la información para la solicitud de tu factura.
            </Typography>

            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                {/* Monto Total a Facturar */}
                <Grid size={12}>
                  <TextField
                    error={!!errors.monto}
                    helperText={errors.monto}
                    required
                    fullWidth
                    label="Monto Total (Incluye IVA)"
                    name="monto"
                    type="number"
                    value={formValues.monto}
                    onChange={handleChange}
                    placeholder="0.00"
                    slotProps={{
                      input: {
                        startAdornment: <span style={{ marginRight: '8px', color: '#666' }}>$</span>,
                        endAdornment: <span style={{ marginLeft: '8px', color: '#666' }}>MXN</span>
                      }
                    }}
                  />
                </Grid>

                <Grid size={12} mt={2}>
                  <Typography className="titleMain" variant="h5" component="h2">
                    Datos de la empresa
                  </Typography>
                  <Typography variant="caption">
                    Completa la información de facturación de la empresa.
                  </Typography>
                </Grid>

                {/* Razón Social */}
                <Grid size={12}>
                  <TextField
                    error={!!errors.razonSocial}
                    helperText={errors.razonSocial}
                    required
                    fullWidth
                    label="Razón Social"
                    name="razonSocial"
                    value={formValues.razonSocial}
                    onChange={handleChange}
                    placeholder="Razón social del inversionista o la de la empresa"
                  />
                </Grid>

                {/* RFC y Régimen Fiscal */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    error={!!errors.rfc}
                    helperText={errors.rfc}
                    required
                    fullWidth
                    label="RFC"
                    name="rfc"
                    value={formValues.rfc}
                    onChange={handleChange}
                    placeholder="Clave del Registro Federal de Contribuyentes"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth error={!!errors.regimenFiscal}>
                    <InputLabel id="regimen-label">Régimen Fiscal</InputLabel>
                    <Select
                      labelId="regimen-label"
                      label="Régimen Fiscal"
                      name="regimenFiscal"
                      value={formValues.regimenFiscal}
                      onChange={handleChange}
                    >
                      <MenuItem value="">Selecciona</MenuItem>
                      <MenuItem value="1">Régimen de Sueldos y Salarios</MenuItem>
                      <MenuItem value="2">Régimen de Actividades Empresariales y Profesionales</MenuItem>
                      <MenuItem value="3">Régimen de Incorporación Fiscal (RIF)</MenuItem>
                      <MenuItem value="4">Régimen de Personas Morales</MenuItem>
                      <MenuItem value="5">Régimen de Arrendamiento</MenuItem>
                      <MenuItem value="6">Régimen de Intermedios</MenuItem>
                      <MenuItem value="7">Régimen de Enajenación de Bienes</MenuItem>
                      <MenuItem value="8">Régimen de Consolidación Fiscal</MenuItem>
                    </Select>
                    {errors.regimenFiscal && <FormHelperText>{errors.regimenFiscal}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid size={12} mt={2}>
                  <Typography className="titleMain" variant="h5" component="h2">
                    Dirección fiscal
                  </Typography>
                  <Typography variant="caption">
                    * No Obligatorios (Dirección registrada ante el SAT)
                  </Typography>
                </Grid>

                {/* País y Estado */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="pais-label">País</InputLabel>
                    <Select
                      labelId="pais-label"
                      label="País"
                      name="pais"
                      value={formValues.pais}
                      onChange={handleChange}
                    >
                      <MenuItem value="Mexico">México</MenuItem>
                      <MenuItem value="EEUU">EEUU</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="estado-label">Estado</InputLabel>
                    <Select
                      labelId="estado-label"
                      label="Estado"
                      name="estado"
                      value={formValues.estado}
                      onChange={handleChange}
                    >
                      <MenuItem value="">Selecciona</MenuItem>
                      {estados.map((info, i) => (
                        <MenuItem key={i} value={formValues.pais === "EEUU" ? info.name : info.estado}>
                          {formValues.pais === "EEUU" ? info.name : info.estado}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Código Postal y Delegación */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Código Postal"
                    name="cp"
                    value={formValues.cp}
                    onChange={handleChange}
                    placeholder="C.P."
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  {formValues.pais === "EEUU" ? (
                    <TextField
                      fullWidth
                      label="Delegación o Municipio"
                      name="delegacion"
                      value={formValues.delegacion}
                      onChange={handleChange}
                      placeholder="Delegación"
                    />
                  ) : (
                    <FormControl fullWidth>
                      <InputLabel id="delegacion-label">Municipio</InputLabel>
                      <Select
                        labelId="delegacion-label"
                        label="Municipio"
                        name="delegacion"
                        value={formValues.delegacion}
                        onChange={handleChange}
                      >
                        <MenuItem value="">Selecciona</MenuItem>
                        {municipios.map((info) => (
                          <MenuItem key={info} value={info}>{info}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>

                {/* Colonia y Calle */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Colonia"
                    name="colonia"
                    value={formValues.colonia}
                    onChange={handleChange}
                    placeholder="Colonia"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Calle"
                    name="calle"
                    value={formValues.calle}
                    onChange={handleChange}
                    placeholder="Nombre de la calle"
                  />
                </Grid>

                {/* Número exterior y Número interior */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Número exterior"
                    name="numExt"
                    value={formValues.numExt}
                    onChange={handleChange}
                    placeholder="Num ext"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Número interior"
                    name="numInt"
                    value={formValues.numInt}
                    onChange={handleChange}
                    placeholder="Num int (Opcional)"
                  />
                </Grid>

                <Grid size={12} mt={3}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{ height: 48 }}
                  >
                    Enviar Solicitud
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RequestInvoice;
